import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { trustLabel, trustColor } from "../../utils/format";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useIsMobile } from "../../hooks/useIsMobile";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { apiFetch } from "../../utils/api";

interface DashStats {
  total_views: number;
  nda_requests: number;
  total_inquiries: number;
  unread_inquiries: number;
}

export function DashboardPage() {
  usePageTitle("\uB300\uC2DC\uBCF4\uB4DC");
  const { broker, refresh } = useAuth();
  const m = useIsMobile();
  const [stats, setStats] = useState<DashStats | null>(null);

  useEffect(() => {
    refresh();
    apiFetch<DashStats>("/api/dashboard/stats")
      .then(setStats)
      .catch(() => {/* 통계 로드 실패 시 0 표시 */});
  }, []);

  if (!broker) {
    return (
      <div style={styles.loading}>불러오는 중...</div>
    );
  }

  const score = broker.trust_score ?? 50;

  return (
    <div style={styles.container}>
      <AppHeader />

      <main style={{ ...styles.main, maxWidth: 1200, padding: m ? "28px 16px 48px" : "28px 32px 48px" }}>
        {/* 웰컴 헤더 */}
        <div style={styles.pageHeader}>
          <p style={styles.pageGreeting}>안녕하세요,</p>
          <h1 style={styles.pageTitle}>
            <span style={{ color: "var(--lt-gold)" }}>{broker.name}</span>님의 대시보드
          </h1>
        </div>

        {/* 프로필 카드 */}
        <div style={styles.profileCard}>
          <div style={styles.profileTop}>
            <div style={styles.avatar}>
              {broker.name.charAt(0)}
            </div>
            <div>
              <h2 style={styles.brokerName}>{broker.name}</h2>
              <p style={styles.officeName}>{broker.office_name}</p>
            </div>
          </div>

          <div style={styles.trustArea}>
            <div style={styles.trustScore}>
              <span
                style={{
                  ...styles.trustBadge,
                  background: trustColor(score),
                }}
              >
                {score}
              </span>
              <span style={styles.trustText}>
                신뢰도 {trustLabel(score)}
              </span>
            </div>
            {!broker.is_verified && (
              <div style={styles.verifyBadge}>
                자격증 인증 대기중
              </div>
            )}
            {broker.is_verified && (
              <div style={styles.verifiedBadge}>
                인증 완료
              </div>
            )}
          </div>
        </div>

        {/* 통계 카드 */}
        <div style={{ ...styles.statsGrid, gridTemplateColumns: m ? "1fr 1fr" : "repeat(4, 1fr)" }}>
          <StatCard label="등록 매물" value={broker.total_listings ?? 0} unit="건" accent="#10B981" />
          <StatCard label="성사 거래" value={broker.total_deals_closed ?? 0} unit="건" accent="#3B82F6" />
          <StatCard label="받은 NDA" value={stats?.nda_requests ?? 0} unit="건" accent="#D4A853" />
          <StatCard label="미확인 문의" value={stats?.unread_inquiries ?? 0} unit="건" accent="#F59E0B" />
        </div>

        {/* 빠른 액션 */}
        <div style={{ ...styles.actionGrid, gridTemplateColumns: m ? "1fr 1fr" : "repeat(3, 1fr)" }}>
          <Link to="/listings/new" style={{ textDecoration: "none" }}>
            <ActionCard title="매물 등록" desc="블라인드 매물을 등록합니다" icon="+" accent="#D4A853" />
          </Link>
          <Link to="/listings" style={{ textDecoration: "none" }}>
            <ActionCard title="매물 탐색" desc="다른 중개사의 매물을 찾습니다" icon={"\uD83D\uDD0D"} accent="#3B82F6" />
          </Link>
          <Link to="/mypage" style={{ textDecoration: "none" }}>
            <ActionCard title="마이페이지" desc="내 정보와 매물을 확인합니다" icon={"\uD83D\uDC64"} accent="#10B981" />
          </Link>
        </div>

      </main>
      <AppFooter />
    </div>
  );
}

function StatCard({
  label,
  value,
  unit,
  accent = "var(--lt-gold)",
}: {
  label: string;
  value: number;
  unit: string;
  accent?: string;
}) {
  return (
    <div className="lt-stat-card" style={{ ...styles.statCard, borderLeftColor: accent }}>
      <p style={{ ...styles.statValue, color: accent }}>
        {value}
        <span style={styles.statUnit}>{unit}</span>
      </p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

function ActionCard({
  title,
  desc,
  icon,
  accent = "var(--lt-gold)",
  disabled = false,
}: {
  title: string;
  desc: string;
  icon: string;
  accent?: string;
  disabled?: boolean;
}) {
  return (
    <div
      className="lt-action-card"
      style={{
        ...styles.actionCard,
        borderLeftColor: accent,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span style={{ ...styles.actionIcon, color: accent }}>{icon}</span>
      <h3 style={styles.actionTitle}>{title}</h3>
      <p style={styles.actionDesc}>{desc}</p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    color: "var(--text-muted)",
  },
  container: {
    minHeight: "100vh",
    background: "var(--bg-primary)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderBottom: "1px solid var(--border-light)",
    background: "var(--bg-card)",
  },
  headerLogo: {
    fontSize: 22,
    fontWeight: 800,
    color: "var(--text-primary)",
    margin: 0,
  },
  adminLink: {
    padding: "8px 16px",
    borderRadius: "var(--radius-md)",
    background: "var(--lt-red)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  },
  logoutBtn: {
    padding: "8px 16px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "transparent",
    color: "var(--text-secondary)",
    fontSize: 13,
    cursor: "pointer",
  },
  main: {
    maxWidth: 640,
    margin: "0 auto",
    padding: "28px 16px 48px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 20,
  },
  // ── 웰컴 헤더 ──
  pageHeader: {
    paddingBottom: 4,
  },
  pageGreeting: {
    fontSize: 13,
    color: "var(--text-muted)",
    margin: "0 0 4px",
    letterSpacing: "0.3px",
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: "var(--text-primary)",
    margin: 0,
    letterSpacing: "-0.3px",
  },
  // ── 프로필 카드 ──
  profileCard: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderTop: "2px solid var(--lt-gold)",
    borderRadius: "var(--radius-xl)",
    padding: 24,
    boxShadow: "var(--shadow-md)",
  },
  profileTop: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 16,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #B8972E, #D4AF37)",
    color: "#0F172A",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 800,
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(212,168,83,0.3)",
  },
  brokerName: {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: 0,
  },
  officeName: {
    fontSize: 13,
    color: "var(--text-muted)",
    margin: "2px 0 0",
  },
  trustArea: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 16,
    borderTop: "1px solid var(--border-light)",
  },
  trustScore: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  trustBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: "50%",
    color: "#fff",
    fontSize: 14,
    fontWeight: 800,
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },
  trustText: {
    fontSize: 14,
    color: "var(--text-secondary)",
    fontWeight: 500,
  },
  verifyBadge: {
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(245, 158, 11, 0.12)",
    color: "var(--lt-orange)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.3px",
  },
  verifiedBadge: {
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(16, 185, 129, 0.12)",
    color: "var(--lt-green)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.3px",
  },
  // ── 통계 카드 ──
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  statCard: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderLeft: "3px solid var(--lt-gold)",
    borderRadius: "var(--radius-lg)",
    padding: "20px 16px",
    textAlign: "center" as const,
    boxShadow: "var(--shadow-sm)",
  },
  statValue: {
    fontSize: 32,
    fontWeight: 800,
    color: "var(--lt-gold)",
    margin: 0,
    lineHeight: 1.1,
  },
  statUnit: {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--text-muted)",
    marginLeft: 3,
  },
  statLabel: {
    fontSize: 13,
    color: "#CBD5E1",
    margin: "8px 0 0",
    letterSpacing: "0.4px",
    textTransform: "uppercase" as const,
  },
  // ── 액션 카드 ──
  actionGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  actionCard: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderLeft: "3px solid var(--lt-gold)",
    borderRadius: "var(--radius-lg)",
    padding: "22px 16px",
    textAlign: "center" as const,
    boxShadow: "var(--shadow-sm)",
  },
  actionIcon: {
    fontSize: 26,
    display: "block",
    marginBottom: 10,
  },
  actionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: 0,
  },
  actionDesc: {
    fontSize: 14,
    color: "#CBD5E1",
    margin: "5px 0 0",
    lineHeight: 1.4,
  },
  comingSoon: {
    textAlign: "center" as const,
    fontSize: 13,
    color: "var(--text-muted)",
    padding: 20,
  },
};
